import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Copy, Check } from 'lucide-react'

import MagneticEffect from './MagneticEffect'
import SpoilerText from './SpoilerText'

interface ServicesProps {
  onApplyClick: () => void
}

const Services: React.FC<ServicesProps> = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  // Функция для рендеринга описания с спойлером
  const renderDescriptionWithSpoiler = (description: string) => {
    if (description.includes('внутренного магазина?')) {
      const parts = description.split('внутренного магазина?')
      return (
        <>
          {parts[0]}
          <SpoilerText>внутренного магазина?</SpoilerText>
          {parts[1]}
        </>
      )
    }
    return description
  }

  // Функция для копирования в буфер обмена
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  // Блокировка скролла при открытом модальном окне
  React.useEffect(() => {
    if (isModalOpen || isConnectModalOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isModalOpen, isConnectModalOpen])
  
  const botFeatures = [
    {
      id: 1,
      title: "Кадровый аудит",
      description: "Автоматизированная система принятия,повышения,премирования, включающая анализ производительности, и сбор статистики.",
      icon: "👥"
    },
    {
      id: 2,
      title: "Учет контрактов",
      description: "Централизованный учет всех контрактов семьи с системой одобрения и выплатами в игре.",
      icon: "📋"
    },
    {
      id: 3,
      title: "Выплаты премий",
      description: "Система расчета и распределения бонусов на основе контрактов, введения промокодов и внутреннего магазина?",
      icon: "💰"
    },
    {
      id: 4,
      title: "Дейли задания",
      description: "Ежедневные задачи и квесты для поддержания активности команды с системой наград.",
      icon: "📅"
    },
    {
      id: 5,
      title: "ИИ-помощник",
      description: "Интеллектуальный ассистент для ответов на вопросы, связанных с законкой и правилами проекта.",
      icon: "🧠"
    },
    {
      id: 6,
      title: "Мини игры",
      description: "Развлекательные активности для зароботка внутренней валюты: орел и решка, кости, К-М-Б и лотерея.",
      icon: "🎮"
    },
    {
      id: 7,
      title: "Авто каналы",
      description: "Автоматическое создание собственных войс каналов.",
      icon: "📢"
    },
    {
      id: 8,
      title: "Музыка",
      description: "Интегрированный музыкальный бот плеер с поддержкой Yandex Music, YouTube, SoundCloud, и других сервисов.",
      icon: "🎵"
    },
    {
      id: 9,
      title: "Внутренняя валюта",
      description: "Система внутренних токенов.",
      icon: "🪙"
    },
    {
      id: 10,
      title: "Аналитика и отчеты",
      description: "Бот собирает статистику по всем действим семьи, статистику с бота можете посмотреть на сайте.",
      icon: "📊"
    }
  ]
  
  const services = [
    {
      icon: '/content/Bot_logo/EMS_Clerk.png',
      title: 'EMS Clerk',
      description: 'Online',
      color: 'text-red-400',
      bgColor: 'bg-red-400/20',
    },
    {
      icon: '/content/Bot_logo/FIB_Clerk.png',
      title: 'FIB Clerk',
      description: 'Online',
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/20',
    },
    {
      icon: '/content/Bot_logo/LSCSD_Clerk.png',
      title: 'LSCSD Clerk',
      description: 'Online',
      color: 'text-green-400',
      bgColor: 'bg-green-400/20',
    },
    {
      icon: '/content/Bot_logo/LSPD_Clerk.png',
      title: 'LSPD Clerk',
      description: 'Online',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-400/20',
    },
    {
      icon: '/content/hugo_logo.png',
      title: 'img.hugo.family',
      description: 'Online',
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/20',
    },
  ]



  return (
    <section id="services" className="min-h-screen flex items-center bg-dark-900 relative overflow-hidden">
             {/* Background Elements */}
       <div className="absolute inset-0">
         <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
         <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-600/10 rounded-full blur-3xl"></div>
         
         {/* Floating Icons - Hidden on Mobile */}
         <div className="hidden md:block">
                   {/* Floating Icons */}
          <motion.div
            className="absolute top-20 left-20 text-4xl text-primary-400/10"
            animate={{
              y: [0, -50, 0],
              x: [0, 20, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
              delay: 0,
            }}
          >
            🤖
          </motion.div>
          <motion.div
            className="absolute top-40 right-32 text-3xl text-primary-400/10"
            animate={{
              y: [0, -30, 0],
              x: [0, -15, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear",
              delay: 2,
            }}
          >
            🧠
          </motion.div>
          <motion.div
            className="absolute bottom-40 left-32 text-4xl text-primary-400/10"
            animate={{
              y: [0, -40, 0],
              x: [0, 25, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "linear",
              delay: 4,
            }}
          >
            🛠️
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 text-3xl text-primary-400/10"
            animate={{
              y: [0, -35, 0],
              x: [0, -20, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 16,
              repeat: Infinity,
              ease: "linear",
              delay: 1,
            }}
          >
            ⚙️
          </motion.div>
          <motion.div
            className="absolute top-60 left-1/3 text-3xl text-primary-400/10"
            animate={{
              y: [0, -45, 0],
              x: [0, 15, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 22,
              repeat: Infinity,
              ease: "linear",
              delay: 3,
            }}
          >
            💻
          </motion.div>
          <motion.div
            className="absolute bottom-60 right-1/3 text-4xl text-primary-400/10"
            animate={{
              y: [0, -55, 0],
              x: [0, -25, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 19,
              repeat: Infinity,
              ease: "linear",
              delay: 5,
            }}
          >
            💾
          </motion.div>
          <motion.div
            className="absolute top-1/3 right-1/4 text-3xl text-primary-400/10"
            animate={{
              y: [0, -40, 0],
              x: [0, 30, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 17,
              repeat: Infinity,
              ease: "linear",
              delay: 2.5,
            }}
          >
            🧲
          </motion.div>
          <motion.div
            className="absolute bottom-1/3 left-1/4 text-4xl text-primary-400/10"
            animate={{
              y: [0, -50, 0],
              x: [0, -30, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 21,
              repeat: Infinity,
              ease: "linear",
              delay: 1.5,
            }}
          >
            🌐
          </motion.div>
          <motion.div
            className="absolute top-1/2 left-1/2 text-3xl text-primary-400/10"
            animate={{
              y: [0, -60, 0],
              x: [0, 35, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 24,
              repeat: Infinity,
              ease: "linear",
              delay: 0.5,
            }}
          >
            📱
          </motion.div>
          
          {/* Additional Floating Icons */}
          <motion.div
            className="absolute top-10 right-1/4 text-4xl text-primary-400/10"
            animate={{
              y: [0, -70, 0],
              x: [0, -40, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear",
              delay: 1.2,
            }}
          >
            🤖
          </motion.div>
          <motion.div
            className="absolute top-80 left-1/4 text-3xl text-primary-400/10"
            animate={{
              y: [0, -35, 0],
              x: [0, 45, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "linear",
              delay: 3.5,
            }}
          >
            🧠
          </motion.div>
          <motion.div
            className="absolute bottom-80 right-1/3 text-4xl text-primary-400/10"
            animate={{
              y: [0, -65, 0],
              x: [0, -35, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 23,
              repeat: Infinity,
              ease: "linear",
              delay: 0.8,
            }}
          >
            🛠️
          </motion.div>
          <motion.div
            className="absolute top-1/4 left-1/3 text-3xl text-primary-400/10"
            animate={{
              y: [0, -55, 0],
              x: [0, 25, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 19,
              repeat: Infinity,
              ease: "linear",
              delay: 4.2,
            }}
          >
            ⚙️
          </motion.div>
          <motion.div
            className="absolute bottom-1/4 right-1/4 text-4xl text-primary-400/10"
            animate={{
              y: [0, -45, 0],
              x: [0, -50, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 16,
              repeat: Infinity,
              ease: "linear",
              delay: 2.8,
            }}
          >
            💻
          </motion.div>
          <motion.div
            className="absolute top-2/3 left-1/6 text-3xl text-primary-400/10"
            animate={{
              y: [0, -75, 0],
              x: [0, 40, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 26,
              repeat: Infinity,
              ease: "linear",
              delay: 1.7,
            }}
          >
            💾
          </motion.div>
          <motion.div
            className="absolute bottom-2/3 right-1/6 text-4xl text-primary-400/10"
            animate={{
              y: [0, -30, 0],
              x: [0, -45, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: "linear",
              delay: 3.8,
            }}
          >
            🧲
          </motion.div>
          <motion.div
            className="absolute top-3/4 left-2/3 text-3xl text-primary-400/10"
            animate={{
              y: [0, -80, 0],
              x: [0, 30, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 27,
              repeat: Infinity,
              ease: "linear",
              delay: 0.3,
            }}
          >
            🌐
          </motion.div>
          <motion.div
            className="absolute bottom-3/4 right-2/3 text-4xl text-primary-400/10"
            animate={{
              y: [0, -40, 0],
              x: [0, -55, 0],
              rotate: [0, -360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
              delay: 4.7,
            }}
          >
            📱
          </motion.div>
         </div>
       </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
                     <motion.h2
             initial={{ opacity: 0, y: 30 }}
             animate={inView ? { opacity: 1, y: 0 } : {}}
             transition={{ delay: 0.3, duration: 0.8 }}
             className="text-4xl md:text-5xl font-display font-bold text-white mb-6"
           >
             <span className="gradient-text">Сервисы by HUGO</span>
           </motion.h2>
         </motion.div>

         {/* Services Grid */}
                   <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mb-12 sm:mb-16"
          >
                       {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                className="group"
              >
                                 <MagneticEffect>
                   <div className="bg-dark-800/70 backdrop-blur-sm rounded-2xl p-3 sm:p-4 md:p-6 transition-all duration-300 group-hover:transform group-hover:scale-105 relative overflow-hidden shadow-2xl shadow-black/30">
                   <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-gray-100 transition-colors">
                                          <img 
                        src={service.icon} 
                        alt={service.title}
                        className={`${service.title === 'LSCSD Clerk' ? 'w-8 h-8 sm:w-10 sm:h-10' : 'w-10 h-10 sm:w-12 sm:h-12'} object-contain ${service.title === 'img.hugo.family' ? 'filter brightness-0' : ''}`}
                      />
                   </div>
                  
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">
                    {service.title}
                  </h4>
                  
                  <p className="text-green-400 font-semibold flex items-center mb-3 sm:mb-4 text-sm sm:text-base">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                    {service.description}
                  </p>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-dark-900/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {service.title === 'img.hugo.family' ? (
                      <a 
                        href="https://img.hugoinc.family/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-colors duration-300 text-sm sm:text-base"
                      >
                        Перейти
                      </a>
                    ) : (
                      <button 
                        onClick={() => setIsConnectModalOpen(true)}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-colors duration-300 text-sm sm:text-base w-full sm:w-auto"
                      >
                        Хочу подключить
                      </button>
                    )}
                  </div>
                </div>
              </MagneticEffect>
               </motion.div>
             ))}
         </motion.div>

                   {/* Hugo Inc. Bot Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="bg-gradient-to-r from-primary-600/20 to-purple-600/20 rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-black/30"
          >
                       <div className="flex flex-col sm:flex-row items-center sm:items-start mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-600/30 rounded-full flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 overflow-hidden">
                <img 
                  src="/content/Bot_logo/hugo_sever_logo.png" 
                  alt="Hugo Inc. Bot"
                  className="w-8 h-8 sm:w-12 sm:h-12 object-contain rounded-full"
                />
              </div>
             <div className="text-center sm:text-left">
               <h3 className="text-2xl sm:text-3xl font-display font-bold text-white mb-1 sm:mb-2">
                 Hugo Inc. Bot
               </h3>
               <p className="text-primary-300 text-base sm:text-lg">Основной бот семьи</p>
             </div>
           </div>
           
                       <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 mt-8 sm:mt-12">
              <div className="space-y-3 sm:space-y-4 text-gray-300 text-sm sm:text-base">
               <p>
                 Hugo Inc. Bot - это центральный компонент экосистемы семьи, обеспечивающий полную автоматизацию всех процессов.
               </p>
               <p>
                 Бот интегрирован со всеми сервисами семьи и предоставляет единую точку доступа к функциям управления, коммуникации и мониторинга.
               </p>
               <p>
                 От управления персоналом до финансового учета - Hugo Inc. Bot делает все процессы максимально эффективными и прозрачными.
               </p>
             </div>
             
                                                       <div className="space-y-3 sm:space-y-4">
                 <div className="flex items-center justify-between p-3 sm:p-4 bg-dark-800/50 rounded-lg">
                   <span className="text-gray-300 text-sm sm:text-base">Статус</span>
                   <span className="text-green-400 font-semibold text-sm sm:text-base">Online</span>
                 </div>
                 
                 <div className="mt-4 sm:mt-6">
                   <h4 className="text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Основные функции:</h4>
                                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 sm:gap-x-4 gap-y-1 sm:gap-y-2 text-xs sm:text-sm">
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Кадровый аудит</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Учет контрактов</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Выплаты премий</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Дейли задания</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>ИИ-помощник</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Мини игры</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Авто каналы</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Музыка</span>
                      </div>
                      <div className="flex items-center">
                        <span className="w-2 h-2 bg-primary-400 rounded-full mr-2"></span>
                        <span>Внутренняя валюта</span>
                      </div>
                    </div>
                 </div>
               </div>
           </div>
           
                       <motion.button
              onClick={() => setIsModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-orange-600 to-orange-500 text-white mt-6 sm:mt-8 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-500/25 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span className="relative z-10">Узнать больше о боте</span>
            </motion.button>
          </motion.div>
        </div>
        
        {/* Bot Features Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
                             className="fixed inset-0 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setIsModalOpen(false)}
            >
                             <motion.div
                 initial={{ scale: 0.8, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 exit={{ scale: 0.8, opacity: 0 }}
                                  className="bg-dark-800/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative mx-4"
                 onClick={(e) => e.stopPropagation()}
               >
                                 {/* Header */}
                 <div className="flex items-center justify-between mb-4 sm:mb-6">
                   <h2 className="text-xl sm:text-2xl font-bold text-white">Возможности Hugo Inc. Bot</h2>
                   <button
                     onClick={() => setIsModalOpen(false)}
                     className="text-gray-400 hover:text-white transition-colors p-2 w-12 h-12 flex items-center justify-center rounded-lg hover:bg-dark-700"
                   >
                     ✕
                   </button>
                 </div>
                 

                 
                                  {/* Vertical Scroll Content */}
                                    <div className="space-y-4 sm:space-y-6 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto pr-1 sm:pr-2 hide-scrollbar">
                    {botFeatures.map((feature) => (
                      <div key={feature.id} className="flex items-start space-x-3 sm:space-x-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-2xl sm:text-3xl">
                            {feature.icon}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base sm:text-lg font-bold text-white mb-1 sm:mb-2">
                            {feature.title}
                          </h3>
                          <p className="text-gray-300 leading-relaxed text-xs sm:text-sm">
                            {renderDescriptionWithSpoiler(feature.description)}
                          </p>
                        </div>
                      </div>
                                         ))}
                   </div>
                   
                   {/* Scroll Indicator - Bottom Right */}
                   <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 z-10">
                     <button
                       onClick={() => {
                         const scrollContainer = document.querySelector('.space-y-4.sm\\:space-y-6.max-h-\\[60vh\\].sm\\:max-h-\\[70vh\\].overflow-y-auto');
                         if (scrollContainer) {
                           scrollContainer.scrollBy({
                             top: 200,
                             behavior: 'smooth'
                           });
                         }
                       }}
                       className="flex flex-col items-center text-primary-400 hover:text-primary-300 transition-colors duration-200 p-2 rounded-lg hover:bg-dark-700/50 w-12 h-12"
                       title="Прокрутить вниз"
                     >
                       <svg 
                         className="w-5 h-5 sm:w-6 sm:h-6" 
                         fill="none" 
                         stroke="currentColor" 
                         viewBox="0 0 24 24"
                       >
                         <path 
                           strokeLinecap="round" 
                           strokeLinejoin="round" 
                           strokeWidth={2} 
                           d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                         />
                       </svg>
                       <span className="text-xs mt-1 opacity-70">Еще</span>
                     </button>
                   </div>
               </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Connect Bot Modal */}
        <AnimatePresence>
          {isConnectModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 backdrop-blur-md z-50 flex items-center justify-center p-4"
              onClick={() => setIsConnectModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-dark-800/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 md:p-8 max-w-md w-full shadow-2xl mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Подключение бота</h2>
                  <button
                    onClick={() => setIsConnectModalOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors p-2 w-12 h-12 flex items-center justify-center rounded-lg hover:bg-dark-700"
                  >
                    ✕
                  </button>
                </div>
                
                                 {/* Content */}
                 <div className="text-center">
                   <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-600/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                     <span className="text-2xl sm:text-3xl">🤖</span>
                   </div>
                   <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
                     Обратитесь в Discord
                   </h3>
                   <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                     <p className="text-gray-300 text-base sm:text-lg font-medium">
                       yaroslav1
                     </p>
                     <button
                       onClick={() => copyToClipboard('yaroslav1')}
                       className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors duration-200 group w-12 h-12 flex items-center justify-center"
                       title="Копировать имя пользователя"
                     >
                       {copied ? (
                         <Check className="w-4 h-4 text-green-400" />
                       ) : (
                         <Copy className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
                       )}
                     </button>
                   </div>
                   <p className="text-gray-400 text-xs sm:text-sm">
                     Для подключения бота к вашему серверу свяжитесь с yaroslav1 в Discord
                   </p>
                 </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
   )
}

export default Services 